import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, LogIn, AlertCircle } from "lucide-react";
import { useAuth } from "../auth/AuthContext";

const LoginPage = () => {
  const { user, login, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && user) {
      navigate(location.state?.from || "/admin", { replace: true });
    }
  }, [user, loading, navigate, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err.response?.data?.detail || "Sign in failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-24 min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white p-8 md:p-10 shadow-lg border border-slate-200">
          <div className="text-center mb-8">
            <div className="w-14 h-14 bg-[#0B2343] mx-auto mb-4 flex items-center justify-center">
              <LogIn className="text-[#C89B3C]" size={26} />
            </div>
            <h1 className="font-serif text-3xl font-bold text-slate-900">Agent sign in</h1>
            <p className="text-slate-500 text-sm mt-2">Noble Strategies internal access</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 mb-5 flex items-start gap-2 text-sm">
              <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 focus:border-slate-900 outline-none"
                  data-testid="login-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 focus:border-slate-900 outline-none"
                  data-testid="login-password"
                />
              </div>
              <div className="text-right mt-2">
                <Link to="/forgot-password" className="text-xs text-[#C89B3C] hover:underline">
                  Forgot password?
                </Link>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#0B2343] text-white py-3 font-medium hover:bg-slate-800 transition-colors disabled:opacity-50"
              data-testid="login-submit"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-6 text-center text-xs text-slate-400">
            Agent accounts are created by an administrator.
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
